import { Text } from '@volkswagen-onehub/components-core';
import Axios, { AxiosResponse } from 'axios';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { dpsSend } from 'src/bff';
import {
  FormController,
  removeWhiteSpace,
  useFaContent,
  useGetInstalaciones,
  useGetWaidLitera,
  useOneFormContext,
  formatPrefix,
} from 'src/feature-app';
import { getParamsForm } from 'src/feature-app/get-params-form';
import {
  CarlineBase,
  FormDataGroup,
  FormDataHeaders,
  FormInfo,
  Installation,
  MolecularResponse,
  OneFormState,
  Steps,
} from 'src/types';
import { OneElementTemplate } from 'src/feature-app/Screen/Templates';
import { ThankYouLeads } from '.';
import { formatFields, formatFlags, formatHeaders, formDataBuilder, getDpsFields } from '../format-fields';
import { addRemovedStep, getIndexInSteps, removeStep, resetStepsScreenIndex } from '../helpers/add-remove-steps';
import {
  getCodInterestCarline,
  getDescInterestCarline,
  getOfertasComercialesData,
  getOfertasData,
  getSalesGroup,
} from './helpers';
import { LeadGenericoFormProps } from './LeadGenericoForm';
import { MultipleCTASelection } from 'src/components';
import { boolean } from 'yup';

const isOfertasForm = (formName: string) => formName === 'lead-oferta';
const getDescFormObjective = (formName: string, carline: CarlineBase, headers: FormDataHeaders) =>
  isOfertasForm(formName) && carline?.campaign?.dfo?.ofertas
    ? carline.campaign.dfo.ofertas
    : headers.DESC_FORMOBJECTIVE;

export const horarioContactoOptions = [
  {
    value: 'Mañana',
    detallesPrevStep: 'Por la mañana',
    label: 'Por la mañana',
  },
  {
    value: 'Mediodia',
    detallesPrevStep: 'Al mediodía',
    label: 'Al mediodía',
  },
  {
    value: 'Tarde',
    detallesPrevStep: 'Por la tarde',
    label: 'Por la tarde',
  },
  {
    value: 'A cualquier hora',
    detallesPrevStep: 'A cualquier hora',
    label: 'A cualquier hora',
  },
];

const stepHorario = {
  title: (
    <>
      <Text bold>¿Cúando </Text>prefieres que te llamemos?
      <sup style={{ top: 'auto', fontSize: '100%' }}>*</sup>
    </>
  ),
  fields: (
    <OneElementTemplate
      element={<MultipleCTASelection ctaValues={horarioContactoOptions} name="horarioContacto" nextOnClick />}
    />
  ),
  screenIndex: 100, // Se reiniciará más adelante
  name: 'HorarioContacto',
  outputs: ['horarioContacto'],
  outputsText: ['horarioContacto'],
};

export function LeadGenericoComercialesForm({ steps, leadData, leadInfo, firstStep, lastStep }: LeadGenericoFormProps) {
  const { initializeForm, handleNextStep } = useOneFormContext();
  const { contacto } = useSelector((state: OneFormState) => state.formData.fields);
  const store = useStore();
  const installations = useGetInstalaciones('LCV_DDB_FRM_LEM_REC_LEM'); // Inicialización de las instalaciones
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const faContent = useFaContent();
  const waid = useGetWaidLitera();

  const sendForm = (): Promise<boolean> => {
    const { formData, dealersInfo }: OneFormState = store.getState();
    const { fields, headers, flags } = formData;
    const { selectedInstallation, salesGroup, selectedCarline } = dealersInfo;
    const {
      TXT_V_Installation_Data_Name__c,
      TXT_V_KVPS_code__c,
      FRML_TXT_codVentaProvincia__c,
      TXT_V_Installation_Code__c,
    } = selectedInstallation;

    return new Promise<any>(async (resolve, reject) => {
      const { tc, today, referalUrl, url, device } = getParamsForm();

      const data = {
        ...formatHeaders(headers), // Headers comunes a todos los formularios de leads
        ...formatFields(fields.formFields), // Fields comunes a todos los formularios de leads
        ...formatFlags(flags), // Flags comunes a todos los formularios de leads

        'headers[COD_FORM_ID]': `vw-oneform-${fields.formName}`,
        'headers[DESC_URLORIGIN]': url,
        'headers[DESC_DEVICETYPE]': device,
        'headers[DESC_FORMOBJECTIVE]': getDescFormObjective(fields.formName, selectedCarline, headers),
        'headers[DESC_ID_CAMPAIGN]': selectedCarline?.campaign?.salesforce
          ? selectedCarline.campaign.salesforce
          : '7011n000000ARTWAA4',

        'headers[FECHA_INSERTION_DATE]': today,
        'headers[TC]': tc,

        'fields[DESC_TREATMENT]': fields.trato,
        'fields[DESC_POSTAL_CODE]': fields.postalCode,

        'fields[DESC_NAME]': fields.name,
        'fields[DESC_SURNAME_1]': fields.surname,
        'fields[DESC_SURNAME_2]': fields.secondSurname,
        'fields[DESC_EMAIL_ADDRESS]': removeWhiteSpace(fields.email),
        // 'fields[NUM_CELLPHONE]': fields.cellphone ? `${formatPrefix(fields.prefix)}${fields.cellphone}` : undefined,
        'fields[NUM_CELLPHONE]': fields.cellphone ? `${fields.cellphone}` : undefined,
        'fields[COD_LEAD_ID]': 'LCV_DDB_FRM_LEM_REC_LEM',
        'fields[DESC_VEH_BRAND]': 'Volkswagen Vehículos Comerciales',
        'fields[DESC_FORM_VERSION]': `vw-oneform-${fields.formName}-${process.env.VERSION}`,
        'fields[DESC_DEALER_CODE_KVPS]': TXT_V_KVPS_code__c,
        'fields[DESC_DEALER_CODE]': TXT_V_Installation_Code__c,
        'fields[DESC_DEALER_PROVINCE]': fields.provinceName,
        'fields[COD_DEALER_PROVINCE]': FRML_TXT_codVentaProvincia__c,
        'fields[DESC_DEALER]': TXT_V_Installation_Data_Name__c,
        'fields[COD_INTEREST_CARLINE]': getCodInterestCarline(dealersInfo, fields.carlineCode),
        'fields[DESC_INTEREST_CARLINE]': getDescInterestCarline(dealersInfo, fields.modelo),
        'fields[DESC_PREFERRED_CONTACT_CHANNEL]': fields.contacto,
        'fields[DESC_TRANF_DATA_THIRD]': 'NO INFORMADO',
        'fields[DESC_LSSI]': fields.lssi ? fields.lssi : 'NO INFORMADO',
        'fields[DESC_LOPD_GDPR_PERFILADO]': fields.tmk ? fields.tmk : 'NO INFORMADO',
        'fields[DESC_AUXFIELD15]': fields.contacto === 'Teléfono' ? fields.horarioContacto : undefined,
        ...getSalesGroup(salesGroup),
        ...getDpsFields(faContent),
        ...(await getOfertasComercialesData(formData, dealersInfo)),
      };
      const dataStringified = JSON.stringify(data);
      const dataParsed = JSON.parse(dataStringified);

      const formDataInfo = formDataBuilder(dataParsed);

      const response = await dpsSend(formDataInfo);

      if (response && response.data && response.data.content && response.data.content.code === '200') {
        dispatch({ type: 'SET_FORM_RESPONSE', payload: response.data });
        resolve(true);
      } else {
        resolve(false);
      }
    });
  };

  const updateSteps = (steps: Steps[]) => {
    dispatch({ type: 'UPDATE_STEPS', payload: steps });
    dispatch({ type: 'UPDATE_LAST_STEP', payload: steps[steps.length - 1] });
    dispatch({ type: 'SET_NUMBER_OF_SCREENS', payload: steps.length });
  };

  useEffect(() => {
    if (contacto === 'Teléfono') {
      const indexOfContacto = getIndexInSteps(steps, 'Contacto');
      const newSteps = [...steps];
      newSteps.splice(indexOfContacto + 1, 0, stepHorario);
      const finalSteps = resetStepsScreenIndex(newSteps);

      updateSteps(finalSteps);
      handleNextStep();
    } else if (contacto === 'Email') {
      const { steps } = store.getState().formInfo;
      const indexOfContacto = getIndexInSteps(steps, 'HorarioContacto');

      if (indexOfContacto === -1) {
        return;
      } else {
        const newSteps = removeStep(steps, 'HorarioContacto');
        const finalSteps = resetStepsScreenIndex(newSteps);

        updateSteps(finalSteps);
      }
    }
  }, [contacto]);

  const leadGenericoData: Partial<FormDataGroup> = {
    headers: {
      DESC_BRAND: 'LCV', // OK
      DESC_CAMPAIGNBLOCK: '5. LCV Tácticas', // OK
      DESC_WAID_LITERA: waid ? waid : '',
      DESC_TOKEN: 'LCV_DDB_FRM_LEM_REC_LEM', // OK
      DESC_VN_VO: 'VN', // OK
      DESC_GDPR_ADAPTED: true, // OK
      ...leadData.headers,
    },
    flags: {
      FLAG_ISPROSPECT: 'true',
      FLAG_ISLEAD: 'false',
      FLAG_SENDIMAWEB: 'false',
      FLAG_SENDTOSELLBYTEL: 'true',
      FLAG_SENDTOSCORE: 'true',
      FLAG_GENERATELEM: 'false',
      FLAG_ISCOMPANY: 'false',
    },
    fields: { ...leadData.fields },
  };

  const leadGenericoInfo: Partial<FormInfo> = {
    formStarted: true,
    fullScreen: firstStep.fullScreen ? true : false,
    lastStep,
    sendFormCallback: sendForm,
    numberOfScreens: steps.length,
    screenIndex: firstStep.screenIndex,
    step: firstStep,
    steps,
    notALayer: false,
    stepsHistory: [steps[0].screenIndex],
    thankYouComponent: <ThankYouLeads />,
    ...leadInfo,
  };

  useEffect(() => {
    initializeForm(leadGenericoInfo, leadGenericoData);
    setLoading(false);
  }, []);

  return loading ? null : <FormController steps={steps} screenType="layer-screen" />;
}
